/* global gsap */

import { useRef, useEffect, useState } from 'react';
import FontFaceObserver from 'fontfaceobserver';

const fontObserver = new FontFaceObserver('ABCGintoNordCondensed Bold', {
  weight: 400,
});

const duration = 0.6;

export default () => {
  const ref = useRef<HTMLElement>(null);
  const animation = useRef<GsapTimeline | null>(null);
  const [font, setFont] = useState(false);
  const [image, setImage] = useState(false);

  function animate() {
    if (animation.current) {
      animation.current.play();
    }
  }

  useEffect(() => {
    const imageContainer = ref.current!.querySelectorAll(
      '.image, .sponsor-background'
    );
    const heading = ref.current!.querySelector('h1')!;
    const menuItems = ref.current!.querySelectorAll('nav a');
    const menuButton = ref.current!.querySelector('nav .toggle');
    const name = ref.current!.querySelector('.name span');
    const sponsors = ref.current!.querySelectorAll('.sponsors');

    heading.classList.add('title-appear-enter');

    animation.current = gsap.timeline({
      paused: true,
      onStart: () => {
        heading.classList.add('title-appear-enter-active');
      },
      onComplete: () => {
        heading.classList.remove(
          'title-appear-enter',
          'title-appear-enter-active'
        );
      },
    });
    animation.current
      .fromTo(
        imageContainer,
        duration,
        { opacity: 0, x: '10%' },
        { opacity: 1, x: '0%', clearProps: true }
      )
      .fromTo(
        heading,
        duration * 1.5,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, clearProps: true }
      );

    if (menuButton) {
      animation.current.fromTo(
        menuButton,
        duration,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, clearProps: true },
        `-=${duration}`
      );
    }

    if (menuItems.length) {
      animation.current.fromTo(
        menuItems,
        duration,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, stagger: 0.2, clearProps: true },
        `-=${duration}`
      );
    }

    animation.current.fromTo(
      name,
      duration,
      { opacity: 0, y: '100%' },
      { opacity: 1, y: '0%', clearProps: true },
      `-=${duration}`
    );

    if (sponsors.length) {
      animation.current.fromTo(
        sponsors,
        duration,
        { opacity: 0 },
        { opacity: 1, clearProps: true },
        `-=${duration}`
      );
    }

    return () => {
      animation.current!.pause();
      animation.current!.kill();
      animation.current = null;
    };
  }, []);

  useEffect(() => {
    let cancel = false;

    fontObserver.load(null, 3000).then(() => {
      if (!cancel) {
        setFont(true);
      }
    });

    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    const image = ref.current!.querySelector('img')!;

    function onLoad() {
      setImage(true);
    }

    image.addEventListener('error', onLoad);
    image.addEventListener('load', onLoad);

    if (image.complete && image.naturalHeight !== 0) {
      setImage(true);
    }

    return () => {
      image.removeEventListener('load', onLoad);
    };
  }, []);

  useEffect(() => {
    if (font && image) {
      animate();
    }
  }, [font, image]);

  return [ref, animate] as const;
};
